import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { formatDateTime } from '@clatter/platform';
import { FloatingButton, ImagePreviewDialog } from '@clatter/ui';
import { IconButtonRounded, NgCard, NgCardContent, NgDivider } from '../../UI';
import { CircularLoader } from '../../../shared';
import { useItemThumbnail } from '../../../hooks';
import { formatFileSize, getIconForMimeType } from '../../../helpers';
import { useSelector } from 'react-redux';
import { viewStateSelectors } from '../../../store';
import { FolderManagerMimeTypes } from '../../../interfaces';
import { useAssetDownload } from '../../../hooks';

interface DetailsViewProps {
  selectedItems: FolderManagerItem[];
  viewStateKey?: string;
  onClose: () => void;
  onAction?: (data: any) => void;
  stickyTop?: number;
}

const StyledProperties = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.large};
  width: 100%;
  flex: 1;

  img {
    width: 100%;
    height: auto;
    object-fit: cover;
  }

  dl {
    font-size: ${({ theme }) => theme.fontSize.default};
  }

  dt {
    margin-bottom: ${({ theme }) => theme.spacing.small};
    color: ${({ theme }) => theme.palette.darkGrey};
  }

  dd {
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-all;
    margin-left: 0;
    margin-bottom: ${({ theme }) => theme.spacing.large};
    font-size: ${({ theme }) => theme.fontSize.default};
    max-width: 100%;
    overflow: hidden;
  }
`;

const StyledDetailsWrapper = styled.aside<{ $stickyTop?: number }>`
  position: relative;
  width: 100%;
  max-height: 100%;

  overflow: visible;

  .info-text {
    font-size: ${({ theme }) => theme.fontSize.larger};
  }

  .details-card-sticky {
    position: sticky;
    top: ${({ $stickyTop, theme }) =>
      $stickyTop !== undefined ? `${$stickyTop}px` : `calc(${theme.spacing.medium} + 58px)`};
  }
`;

const StyledDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};

  width: 100%;
  height: 100%;

  .details-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid ${({ theme }) => theme.palette.grey};

    h3 {
      margin-bottom: 0;
    }
  }
`;

const StyledCenteredContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  min-height: 20vh;
`;

const StyledLoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 120px;
`;

const StyledDefaultThumbnail = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: ${({ theme }) => theme.radius.small};
  background-color: ${({ theme }) => theme.palette.grey};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 60px;
    height: 60px;
    opacity: 0.7;
  }
`;

interface DetailsRailThumbnailProps {
  item: FolderManagerItem;
  viewStateKey?: string;
}

const StyledThumbnailWrapper = styled.div`
  position: relative;
`;

const DetailsRailThumbnail: FC<DetailsRailThumbnailProps> = ({ item, viewStateKey }) => {
  const selectedThemeId = useSelector(viewStateSelectors.getSelectedThemeId(viewStateKey || ''));
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const { thumbnailUrl, isLoading, isFetching, isThemeChanging } = useItemThumbnail({
    itemId: item?.id,
    itemType: item?.type,
    thumbnailUrl: item?.thumbnailUrl,
    themeId: selectedThemeId,
  });

  if (isLoading || (isThemeChanging && isFetching)) {
    return (
      <StyledLoaderContainer>
        <CircularLoader size={30} />
      </StyledLoaderContainer>
    );
  }

  if (!thumbnailUrl) {
    return (
      <StyledDefaultThumbnail>
        {getIconForMimeType(item?.type, item?.isShared, item?.isSlideRepositoryFolder)}
      </StyledDefaultThumbnail>
    );
  }

  return (
    <StyledThumbnailWrapper>
      {/* @ts-ignore */}
      <FloatingButton top={5} right={6} onClick={() => setPreviewImage(thumbnailUrl)}>
        <VisibilityOutlinedIcon />
      </FloatingButton>
      <img src={thumbnailUrl} alt={item?.name || 'Asset Thumbnail'} />
      <ImagePreviewDialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage}
        alt={item?.name || 'Asset Preview'}
      />
    </StyledThumbnailWrapper>
  );
};

const StyledSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DetailsRailProperties = ({
  data,
  viewStateKey,
  onAction,
}: {
  data: FolderManagerItem<SlideFromApi> | FolderManagerItem;
  viewStateKey?: string;
  onAction?: (data: any) => void;
}) => {
  if (data.type === FolderManagerMimeTypes.SLIDE) {
    const { downloadAssetVersionById } = useAssetDownload();

    const computed = useMemo(() => {
      let presentation = data?.itemData || {};
      let slideData: {
        versionNumber: number | null;
        versionId: number | null;
        slideNumber: number | null;
        displayName: string;
      } = {
        displayName: data.displayName || data.name,
        versionNumber: null,
        versionId: null,
        slideNumber: data?.itemData?.slideData?.SlideNumber,
      };

      if (data.type === FolderManagerMimeTypes.SLIDE && data?.itemData?.Asset) {
        const assetVersionId = data.itemData?.AssetVersionId;
        const versionData: IAssetVersion = data?.itemData?.Asset?.Versions.find(
          (version: IAssetVersion) => version.ID === assetVersionId,
        );

        const assetData: AssetFromApi = data.itemData?.Asset;

        slideData.versionNumber = versionData.VersionNumber;
        slideData.versionId = versionData.ID;

        presentation = {
          id: assetData.ID,
          displayName: assetData.DisplayName,
          fileSize: versionData.FileSize,
          owner: {
            Email: assetData.Owner?.Email,
          },
          createdBy: {
            Email: assetData.CreatedBy?.Email,
          },
          createdAt: assetData.CreatedAt,
          updatedAt: assetData.UpdatedAt,
          trashedAt: assetData.TrashedAt,
        };
      } else if (data.type === FolderManagerMimeTypes.SLIDE && data?.itemData?.currentVersion) {
        const itemData = data.itemData;
        const currentVersion = itemData.currentVersion;

        slideData.versionNumber = currentVersion.VersionNumber;
        slideData.versionId = currentVersion.ID;
        slideData.slideNumber = itemData.slideData?.SlideNumber;

        presentation = {
          id: itemData.id,
          displayName: itemData.name,
          fileSize: currentVersion.FileSize,
          owner: {
            Email: itemData.owner?.Email,
          },
          createdBy: {
            Email: itemData.createdBy?.Email,
          },
          createdAt: itemData.createdAt,
          updatedAt: itemData.updatedAt,
          trashedAt: itemData.trashedAt,
        };
      }

      return {
        presentation,
        slideData,
      };
    }, [data]);

    const parentAssetId = computed?.presentation?.id;

    return (
      <StyledProperties>
        <DetailsRailThumbnail item={data} viewStateKey={viewStateKey} />
        <>
          <dl>
            <dt>Name</dt>
            <dd>{computed?.slideData.displayName}</dd>

            {computed?.slideData?.slideNumber != null && (
              <>
                <dt>Slide Number</dt>
                <dd>{computed.slideData.slideNumber}</dd>
              </>
            )}

            <NgDivider />

            <StyledSectionHeader>
              <dt>Presentation</dt>
              {/* {parentAssetId && onAction && (
                  <IconButtonRounded
                    title="Download Presentation"
                    icon={<DownloadOutlinedIcon fontSize="small" />}
                    onClick={() => onAction({ actionType: FolderManagerActionTypes.DOWNLOAD_DECK, payload: data })}
                    testId="details-rail-download-presentation"
                    materialIcon
                    clear
                  />
                )} */}
            </StyledSectionHeader>
            <dd></dd>

            <dt>Name</dt>
            <dd>{computed?.presentation?.displayName}</dd>

            <StyledSectionHeader>
              <dt>Version Number</dt>
              {parentAssetId && computed.slideData.versionId && (
                <IconButtonRounded
                  title="Download Version"
                  icon={<DownloadOutlinedIcon fontSize="small" />}
                  onClick={() =>
                    downloadAssetVersionById({
                      assetId: parentAssetId,
                      assetVersionId: computed.slideData.versionId!,
                    })
                  }
                  testId="details-rail-download-version"
                  materialIcon
                  clear
                />
              )}
            </StyledSectionHeader>
            <dd>{computed?.slideData?.versionNumber}</dd>

            <dt>Owner</dt>
            <dd>{computed.presentation?.owner?.Email || '-'}</dd>

            <dt>Creator</dt>
            <dd>{computed?.presentation?.createdBy?.Email || '-'}</dd>

            <dt>File Size</dt>
            <dd>{computed?.presentation?.fileSize ? formatFileSize(computed?.presentation.fileSize) : '-'}</dd>

            <dt>Created</dt>
            <dd>{formatDateTime(computed?.presentation?.createdAt)}</dd>

            <dt>Modified</dt>
            <dd>{computed?.presentation?.updatedAt ? formatDateTime(computed?.presentation?.updatedAt) : '-'}</dd>

            {computed?.presentation?.trashedAt && (
              <>
                <dt>Deleted</dt>
                <dd>{formatDateTime(computed?.presentation?.trashedAt)}</dd>
              </>
            )}
          </dl>
        </>
      </StyledProperties>
    );
  }

  return (
    <StyledProperties>
      <DetailsRailThumbnail item={data} viewStateKey={viewStateKey} />
      <dl>
        <dt>Name</dt>
        <dd>{data?.name || data?.displayName}</dd>

        <dt>Owner</dt>
        <dd>{data?.owner?.Email || '-'}</dd>

        <dt>Creator</dt>
        <dd>{data?.createdBy?.Email || '-'}</dd>

        <dt>File Size</dt>
        <dd>{data?.fileSize ? formatFileSize(data.fileSize) : '-'}</dd>

        <dt>Created</dt>
        <dd>{formatDateTime(data?.createdAt)}</dd>

        <dt>Modified</dt>
        <dd>{data?.updatedAt ? formatDateTime(data?.updatedAt) : '-'}</dd>

        {data?.trashedAt && (
          <>
            <dt>Deleted</dt>
            <dd>{formatDateTime(data?.trashedAt)}</dd>
          </>
        )}
      </dl>
    </StyledProperties>
  );
};

export const DetailsRail: FC<DetailsViewProps> = ({
  selectedItems = [],
  onClose,
  onAction,
  stickyTop,
  viewStateKey,
}) => (
  <StyledDetailsWrapper $stickyTop={stickyTop}>
    <div className="details-card-sticky">
      <NgCard>
        <NgCardContent>
          {selectedItems?.length <= 0 && (
            <StyledCenteredContainer>
              <p className="info-text">No selected item.</p>
            </StyledCenteredContainer>
          )}

          {selectedItems?.length > 1 && (
            <StyledCenteredContainer>
              <p className="info-text">{selectedItems.length} selected.</p>
            </StyledCenteredContainer>
          )}

          {selectedItems?.length === 1 && (
            <StyledDetails>
              <div className="details-header">
                <h3>Details</h3>

                <IconButtonRounded title="Close" icon={<CloseIcon />} onClick={onClose} filled clear />
              </div>

              <DetailsRailProperties data={selectedItems[0]} viewStateKey={viewStateKey} onAction={onAction} />
            </StyledDetails>
          )}
        </NgCardContent>
      </NgCard>
    </div>
  </StyledDetailsWrapper>
);
